import React from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { ModalLayout } from '@/features/modal/modals/ModalLayout';
import { Button } from '@/components/buttons/Button';
import { TranslatedRichText } from '@/components/TranslatedRichText';
import { BaseModalProps } from '@/features/modal/types';
import { REGION_CODE_ALBERTA, ROC_SITE_URL, matchesRegion } from '@/constants/constants';
import { selectDetectedRegion } from '@/store/selectors/commonSelectors';
import { getAlbertaRedirectUrl, redirectToExternalSite } from '@/utils/alberta-redirect';

export const LocationGateModal = ({ closeModal }: BaseModalProps): JSX.Element => {
  const intl = useIntl();
  const detectedRegion = useSelector(selectDetectedRegion);
  const isAlberta = matchesRegion(detectedRegion.regionCode, REGION_CODE_ALBERTA);

  const headerKey = isAlberta ? 'location.gate.alberta.header' : 'location.gate.other.header';
  const textKey = isAlberta ? 'location.gate.alberta.text' : 'location.gate.other.text';
  const ctaGoKey = isAlberta ? 'location.gate.alberta.cta.go' : 'location.gate.other.cta.go';
  const ctaStayKey = isAlberta ? 'location.gate.alberta.cta.stay' : 'location.gate.other.cta.stay';
  const redirectUrl = isAlberta ? getAlbertaRedirectUrl('ont', false, false) : ROC_SITE_URL;

  const onGo = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      return; // let the browser handle modifier/middle clicks natively
    }
    e.preventDefault();
    redirectToExternalSite(redirectUrl);
  };

  return (
    <ModalLayout data-testid="location-gate-modal" close={closeModal}>
      <div className="w-full text-center grid grid-cols-1 gap-6">
        <h1 className="text-xl font-bold">{intl.formatMessage({ id: headerKey })}</h1>
        <TranslatedRichText id={textKey} tagName="div" />
        <a
          href={redirectUrl}
          onClick={onGo}
          className="relative w-full rounded-3xl p-3 bg-blue-light hover:bg-cyan-600 text-white text-md text-center font-bold leading-6 transition"
        >
          {intl.formatMessage({ id: ctaGoKey })}
        </a>
        <Button
          onClickHandler={() => closeModal()}
          btnType="outline"
          text={intl.formatMessage({ id: ctaStayKey })}
        />
      </div>
    </ModalLayout>
  );
};
